import React, { useState, useEffect, useCallback } from 'react';
import adminService from '../services/adminService';
import { useToast } from '../hooks/use-toast';
import { Ruler, Clock, DollarSign, Building } from 'lucide-react';

const STATUS_COLORS = {
  new: 'bg-green-100 text-green-800',
  reviewing: 'bg-blue-100 text-blue-800',
  quoted: 'bg-purple-100 text-purple-800',
  closed: 'bg-gray-100 text-gray-600',
};

const AdminBuildToSuit = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const { toast } = useToast();

  const loadRequests = useCallback(async () => {
    try {
      const response = await adminService.getBuildToSuitRequests();
      setRequests(response.data.requests || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load requests', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const updateStatus = async (id, newStatus) => {
    try {
      await adminService.updateBtsStatus(id, newStatus);
      toast({ title: 'Updated', description: 'Request status updated' });
      loadRequests();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-gray-500">Loading...</div>;
  }

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900" data-testid="admin-bts-title">Build-to-Suit Requests</h2>
        <p className="text-gray-600 mt-2">Custom warehouse build requests from prospects</p>
      </div>

      {requests.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-500">
          No build-to-suit requests yet.
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((req) => (
            <div key={req.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-testid={`bts-request-${req.id}`}>
              <div
                className="p-5 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                onClick={() => setExpanded(expanded === req.id ? null : req.id)}
              >
                <div className="flex items-center gap-4">
                  <Building className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="font-semibold text-gray-900">{req.name}</p>
                    <p className="text-sm text-gray-500">{req.company || req.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <select
                    value={req.status}
                    onChange={(e) => { e.stopPropagation(); updateStatus(req.id, e.target.value); }}
                    onClick={(e) => e.stopPropagation()}
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[req.status] || STATUS_COLORS.new}`}
                  >
                    <option value="new">New</option>
                    <option value="reviewing">Reviewing</option>
                    <option value="quoted">Quoted</option>
                    <option value="closed">Closed</option>
                  </select>
                  <span className="text-xs text-gray-400">{new Date(req.created_at).toLocaleDateString()}</span>
                </div>
              </div>

              {expanded === req.id && (
                <div className="border-t border-gray-100 p-5 bg-gray-50">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <Ruler className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Desired Area</p>
                        <p className="text-sm font-medium">{req.desired_area_sqm ? `${req.desired_area_sqm} m2` : 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 text-gray-400 flex items-center justify-center text-xs font-bold">H</div>
                      <div>
                        <p className="text-xs text-gray-500">Clear Height</p>
                        <p className="text-sm font-medium">{req.clear_height_m ? `${req.clear_height_m} m` : 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Timeline</p>
                        <p className="text-sm font-medium">{req.timeline || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Budget</p>
                        <p className="text-sm font-medium">{req.budget_range || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-4 text-sm">
                    <span className="text-gray-500">Docks: <span className="text-gray-900 font-medium">{req.loading_docks || 'N/A'}</span></span>
                    <span className="text-gray-500">Phone: <span className="text-gray-900 font-medium">{req.phone}</span></span>
                    <span className="text-gray-500">Email: <a href={`mailto:${req.email}`} className="text-amber-600 font-medium">{req.email}</a></span>
                  </div>
                  {req.special_requirements && (
                    <div className="mt-3 p-3 bg-white rounded border border-gray-200">
                      <p className="text-xs text-gray-500 mb-1">Special Requirements</p>
                      <p className="text-sm text-gray-700">{req.special_requirements}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminBuildToSuit;
